/**
 * useConversationFlowReducer Hook
 *
 * Unified state management for the conversation flow.
 * Replaces useBookingReducer + visitorProfile useState.
 *
 * Provides:
 * - Discriminated union state (ANSWERING | REDIRECTING | BOOKING)
 * - Memoized action creators
 * - Derived type guards (isBooking, isAnswering, isRedirecting)
 */

import { useMemo, useReducer } from 'react';
import type { VisitorProfile } from '../../services/inboundx';
import {
    type ActiveFlow,
    type ConversationFlowInit,
    conversationFlowReducer,
    getInitialConversationFlowState,
} from '../../utils/widget/conversationFlowReducer';

// ---------------------------------------------------------------------------
// Action creators interface
// ---------------------------------------------------------------------------

export interface FlowActions {
    enterAnswering: () => void;
    enterFormCollecting: () => void;
    enterRedirecting: (redirectUrl: string | null) => void;
    startBookingFlow: (
        ctaId: string,
        redirectUrl: string,
        bookingMessage: string,
        ctaAction?: 'navigate' | 'popup' | 'in-chat'
    ) => void;
    confirmBookingResponse: () => void;
    updateBookingRedirectUrl: (redirectUrl: string) => void;
    updatePendingBookingFields: (fields: string[]) => void;
    completeBookingFlow: () => void;
    cancelBookingFlow: () => void;
    updateVisitorProfile: (visitorProfile: VisitorProfile | null) => void;
    showInChatEmbed: (
        url: string,
        layoutBeforeEmbed?: string,
        postBookingRedirectUrl?: string
    ) => void;
    hideInChatEmbed: () => void;
    reset: () => void;
}

// ---------------------------------------------------------------------------
// Merged state (reducer state + derived helpers)
// ---------------------------------------------------------------------------

export interface FlowState {
    isAnswering: boolean;
    isFormCollecting: boolean;
    isRedirecting: boolean;
    isBooking: boolean;
    isFormAssistant: boolean;
    visitorProfile: VisitorProfile | null;
    /** Booking-specific fields (null when not in BOOKING flow) */
    bookingDetails: Extract<ActiveFlow, { type: 'BOOKING' }> | null;
    /** URL of the calendar embed currently shown in-chat (null when not showing) */
    inChatEmbedUrl: string | null;
    /** Layout the widget was in before the embed opened, so we can restore it */
    layoutBeforeEmbed: string | null;
    /** Where to navigate after the embedded calendar reports a successful booking (null when the active CTA has no post-booking redirect configured) */
    inChatEmbedPostBookingRedirectUrl: string | null;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useConversationFlowReducer(init?: ConversationFlowInit) {
    const [state, dispatch] = useReducer(
        conversationFlowReducer,
        getInitialConversationFlowState(init)
    );

    const actions = useMemo<FlowActions>(
        () => ({
            enterAnswering: () => dispatch({ type: 'ENTER_ANSWERING' }),
            enterFormCollecting: () => dispatch({ type: 'ENTER_FORM_COLLECTING' }),
            enterRedirecting: (redirectUrl: string | null) =>
                dispatch({ type: 'ENTER_REDIRECTING', payload: { redirectUrl } }),
            startBookingFlow: (
                ctaId: string,
                redirectUrl: string,
                bookingMessage: string,
                ctaAction?: 'navigate' | 'popup' | 'in-chat'
            ) =>
                dispatch({
                    type: 'START_BOOKING',
                    payload: { ctaId, redirectUrl, bookingMessage, ctaAction },
                }),
            confirmBookingResponse: () => dispatch({ type: 'CONFIRM_BOOKING_RESPONSE' }),
            updateBookingRedirectUrl: (redirectUrl: string) =>
                dispatch({ type: 'UPDATE_BOOKING_REDIRECT_URL', payload: { redirectUrl } }),
            updatePendingBookingFields: (fields: string[]) =>
                dispatch({ type: 'UPDATE_PENDING_BOOKING_FIELDS', payload: { fields } }),
            completeBookingFlow: () => dispatch({ type: 'COMPLETE_BOOKING' }),
            cancelBookingFlow: () => dispatch({ type: 'CANCEL_BOOKING' }),
            updateVisitorProfile: (visitorProfile: VisitorProfile | null) =>
                dispatch({ type: 'UPDATE_VISITOR_PROFILE', payload: { visitorProfile } }),
            showInChatEmbed: (
                url: string,
                layoutBeforeEmbed?: string,
                postBookingRedirectUrl?: string
            ) =>
                dispatch({
                    type: 'SHOW_IN_CHAT_EMBED',
                    payload: { url, layoutBeforeEmbed, postBookingRedirectUrl },
                }),
            hideInChatEmbed: () => dispatch({ type: 'HIDE_IN_CHAT_EMBED' }),
            reset: () => dispatch({ type: 'RESET' }),
        }),
        []
    );

    const flowState = useMemo<FlowState>(() => {
        const { activeFlow } = state;
        return {
            isAnswering: activeFlow.type === 'ANSWERING',
            isFormCollecting: activeFlow.type === 'FORM_COLLECTING',
            isRedirecting: activeFlow.type === 'REDIRECTING',
            isBooking: activeFlow.type === 'BOOKING',
            isFormAssistant: state.isFormAssistant,
            visitorProfile: state.visitorProfile,
            bookingDetails: activeFlow.type === 'BOOKING' ? activeFlow : null,
            inChatEmbedUrl: state.inChatEmbedUrl,
            layoutBeforeEmbed: state.layoutBeforeEmbed,
            inChatEmbedPostBookingRedirectUrl: state.inChatEmbedPostBookingRedirectUrl,
        };
    }, [state]);

    return { flowState, actions };
}
